<template>
  <v-container fluid class="pa-4">

    <!-- ================= Filters Section ================= -->
      <v-row dense>
        <v-col cols="6" sm="12">
          <v-card class="pa-4 mb-4 rounded-lg" elevation="2">
            <v-row dense justify="space-between">
              <!-- <v-col cols="4" sm="4">
                <v-autocomplete
                  v-model="filters.branch_id"
                  :items="branches"
                  item-title="name"
                  item-value="id"
                  label="Select Branch"
                  density="compact"
                  variant="outlined"
                />
              </v-col> -->

              <v-col cols="4" sm="4">
                <v-date-input
                  v-model="filters.from_date"
                  label="From Date"
                  density="compact"
                  variant="outlined"
                  prepend-icon=""
                  prepend-inner-icon="mdi mdi-calendar-month"
                  single-line
                  bg-color="grey-lighten-4"
                  autocomplete="off"
                  hide-details
                />
              </v-col>

              <v-col cols="4" sm="4">
                <v-date-input
                  v-model="filters.to_date"
                  label="To Date"
                  density="compact"
                  variant="outlined"
                  prepend-icon=""
                  prepend-inner-icon="mdi mdi-calendar-month"
                  single-line
                  bg-color="grey-lighten-4"
                  autocomplete="off"
                  hide-details
                />
              </v-col>

               <v-col cols="2" sm="2" class="d-flex align-center">
                  <v-btn color="primary" block @click="loadDashboard">
                      <v-icon left>mdi-magnify</v-icon> Filter
                  </v-btn>
                </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>


    <!-- ================= Summary Cards ================= -->
    <v-row dense>
      <v-col cols="12" md="3" v-for="card in summaryCards" :key="card.label">
        <v-card class="pa-4 rounded-lg d-flex bg-gray" elevation="2" style="min-height: 113px;">
          <v-row align="center" justify="space-between">
            <v-col>
              <v-icon color="primary">{{ card.icon }}</v-icon>
              <div class="text-subtitle-2 text-grey-darken-1">{{ card.label }}</div>
            </v-col>
            <v-col class="text-right">
              <v-chip color="success">
                  {{ card.value }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>


    <!-- ================= Low Stock ================= -->
    <v-card rounded="lg" class="pa-4 mt-6">
      <div class="text-h6 mb-3">Low Stock Alert</div>

      <v-data-table
        :headers="lowStockHeaders"
        :items="dashboard.low_stock"
        density="compact"
        class="elevation-1"
        hide-default-footer
      >
      <template v-slot:item.index="{ index }">
        {{ ++index }}
      </template>
      </v-data-table>
    </v-card>


    <!-- ================= Stock Movement Chart ================= -->
    <v-card rounded="lg" class="pa-4 mt-6">
      <div class="text-h6 mb-3">Stock Movement (In/Out)</div>
      <apexchart
        type="line"
        height="300"
        :options="stockMovementOptions"
        :series="stockMovementSeries"
      />
    </v-card>


    <!-- ================= Fast Moving Items ================= -->
    <v-card rounded="lg" class="pa-4 mt-6">
      <div class="text-h6 mb-3">Fast Moving Items</div>
      <v-data-table
        :headers="movementHeaders"
        :items="dashboard.fast_moving"
        density="compact"
        class="elevation-1"
        hide-default-footer
      >
      <template v-slot:item.index="{ index }">
        {{ ++index }}
      </template>
      </v-data-table>
    </v-card>


    <!-- ================= Slow Moving Items ================= -->
    <v-card rounded="lg" class="pa-4 mt-6">
      <div class="text-h6 mb-3">Slow Moving Items</div>
      <v-data-table
        :headers="movementHeaders"
        :items="dashboard.slow_moving"
        density="compact"
        class="elevation-1"
        hide-default-footer
      >
      <template v-slot:item.index="{ index }">
        {{ ++index }}
      </template>
      </v-data-table>
    </v-card>


    <!-- ================= Warehouse Stock Summary ================= -->
    <v-card rounded="lg" class="pa-4 mt-6">
      <div class="text-h6 mb-3">Warehouse Wise Stock</div>
      <v-data-table
        :headers="warehouseHeaders"
        :items="dashboard.warehouse_stock"
        density="compact"
        class="elevation-1"
        hide-default-footer
      >
        <template v-slot:item.index="{ index }">
          {{ ++index }}
        </template>
       </v-data-table>
    </v-card>

  </v-container>
</template>


<script setup>
import { onMounted, reactive, ref, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import ApexChart from "vue3-apexcharts";
import formattedDate from "@/plugins/dateFormatter";
import { fileServerUrl } from "@/store/api";

const store = useStore();

const baseURL = import.meta.env.VITE_API_BASE_URL || "";

const dashboard = reactive({
  summary: {},
  low_stock: [],
  movement_chart: [],
  fast_moving: [],
  slow_moving: [],
  warehouse_stock: []
});

const branches = ref([]);

const filters = reactive({
  branch_id: "",
  from_date: "",
  to_date: ""
});

// Summary Card Data Formatting
const summaryCards = computed(() => [
  { label: "Total Stock Value", value: dashboard.summary.total_stock_value, icon: 'mdi-finance' ?? 0 },
  { label: "Total Products", value: dashboard.summary.total_products, icon: 'mdi-cube-outline' ?? 0 },
  { label: "Today Stock In", value: dashboard.summary.today_stock_in, icon: 'mdi-tray-arrow-up' ?? 0 },
  { label: "Today Stock Out", value: dashboard.summary.today_stock_out, icon: 'mdi-tray-arrow-down' ?? 0 }
]);

const lowStockHeaders = [
  { title: "#SL", align: "center", key: "index", width: "7%", sortable: false, },
  { title: "Product", align:"center", key: "product_name", sortable: false },
  { title: "Warehouse", align:"center", key: "warehouse_name", sortable: false },
  { title: "Available Qty", align:"center", key: "available_qty", sortable: false }
];

const movementHeaders = [
  { title: "#SL", align: "center", key: "index", width: "7%", sortable: false, },
  { title: "Product", align:"center", key: "product_name", sortable: false },
  { title: "Qty", align:"center", key: "qty", align:"center", sortable: false }
];

const warehouseHeaders = [
  { title: "#SL", align: "center", key: "index", width: "7%", sortable: false, },
  { title: "Warehouse", key: "warehouse_name", align: "center", sortable: false },
  { title: "Total Qty", key: "total_qty", align:"center", sortable: false },
  { title: "Total Value", key: "total_value", align:"center", sortable: false  }
];

const stockMovementOptions = ref({
  chart: {
    id: "stock_movement",
    toolbar: { show: false },
    zoom: { enabled: false }
  },

  stroke: {
    curve: "smooth",
    width: 3
  },

  xaxis: {
    categories: [],
    labels: { rotate: -45 }
  },

  yaxis: {
    title: { text: "Quantity" }
  },

  dataLabels: {
    enabled: false
  },

  colors: ["#3b82f6", "#ef4444"],

  legend: {
    position: "top"
  },

  grid: {
    borderColor: "#e5e7eb"
  }
});

const stockMovementSeries = ref([
  { name: "Stock In", data: [] },
  { name: "Stock Out", data: [] }
]);

function loadMovementChart() {
  const labels = dashboard.movement_chart.map(i => i.date);

  stockMovementOptions.value.xaxis.categories = labels;

  stockMovementSeries.value = [
    {
      name: "Stock In",
      data: dashboard.movement_chart.map(i => i.in_qty)
    },
    {
      name: "Stock Out",
      data: dashboard.movement_chart.map(i => i.out_qty)
    }
  ];
}

async function loadDashboard() {
  // filters.value.from_date = formattedDate(filters.value.from_date);
  // filters.value.to_date = formattedDate(filters.value.to_date);
  const response = await axios.get(`${baseURL}/auth/inventory/dashboard`, filters);
  Object.assign(dashboard, response.data.data);
  loadMovementChart();
}

// async function loadBranches() {
//   const res = await axios.get("/branches");
//   branches.value = res.data.data;
// }

onMounted(() => {
  // loadBranches();
  loadDashboard();
});
</script>

<style>
.dashboard-card {
  background: linear-gradient(135deg, #f8fafc, #eef2ff);
  border: 1px solid #e5e7eb;
}
</style>
